// Signup-source (attribution) capture — ClickUp 86cb74c1n. First-touch only:
// the answer to "which channel got them in the door", not "which channel
// they last saw" (see internal/storage/migrations/0146_add_signup_source.sql
// for the backend rationale). All parsing/validation here is client-side
// defense-in-depth only — the backend re-validates and is authoritative.
//
// Non-essential (marketing-attribution) cookie — ePrivacy Art. 5(3) / Polish
// Telecoms Law Art. 173 requires consent BEFORE it is set. Gated on the same
// `hasAnalyticsConsent()` flag GA uses (frontend/src/utils/analytics.ts).
// If consent isn't granted at the moment of the call, we simply don't
// capture — we deliberately do NOT cache the raw referrer/UTM pre-consent
// to write it later, since that would just move the same non-essential
// data through a different mechanism and defeat the consent gate. A
// visitor who consents only after their first page load loses their true
// first-touch data; that's an accepted trade-off for correctness.
import { hasAnalyticsConsent } from './analytics'

export const SIGNUP_SOURCE_COOKIE_NAME = 'dg_signup_source'

const NINETY_DAYS_SECONDS = 90 * 24 * 60 * 60
const MAX_VALUE_LENGTH = 100

// Sanitizes a raw utm/referrer value: trims, caps length, allowlists
// charset. Mirrors (but does not replace) the backend's authoritative
// validation in internal/attribution/signup_source.go.
function sanitizeSourceValue(value: string): string | null {
  const trimmed = value.trim()
  if (!trimmed) return null
  // ponytail: simple allowlist strip rather than a full grammar — matches
  // the "fail open to direct" theme of this feature, not a security gate.
  const stripped = trimmed
    .replace(/[^a-zA-Z0-9._-]/g, '')
    .slice(0, MAX_VALUE_LENGTH)
  return stripped || null
}

function setCookie(value: string): void {
  const secure = window.location.protocol === 'https:' ? '; Secure' : ''
  document.cookie = `${SIGNUP_SOURCE_COOKIE_NAME}=${value}; Path=/; Max-Age=${NINETY_DAYS_SECONDS}; SameSite=Lax${secure}`
}

function getCookie(name: string): string | null {
  const match = document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${name}=`))
  return match ? match.slice(name.length + 1) : null
}

// Captures first-touch attribution into a first-party cookie, exactly
// once. No-ops if the cookie already exists.
export function captureSignupSourceOnce(
  locationSearch: string,
  referrer: string,
  currentHostname: string
): void {
  if (!hasAnalyticsConsent()) return
  if (getCookie(SIGNUP_SOURCE_COOKIE_NAME) !== null) return

  const params = new URLSearchParams(locationSearch)
  const utmRaw = params.get('utm_source') || params.get('utm_medium')
  const utmValue = utmRaw ? sanitizeSourceValue(utmRaw) : null
  if (utmValue) {
    setCookie(`utm:${utmValue}`)
    return
  }

  if (referrer) {
    try {
      const referrerHost = new URL(referrer).hostname
      if (referrerHost && referrerHost !== currentHostname) {
        setCookie(`referrer:${referrerHost}`)
        return
      }
    } catch {
      // malformed referrer — fall through to direct
    }
  }

  setCookie('direct')
}

// Reads the cookie (if present) for sending at registration time.
export function readSignupSourceHint(): string | null {
  return getCookie(SIGNUP_SOURCE_COOKIE_NAME)
}
